import { faCalendarCheck, faClose } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useTranslation } from "react-i18next"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import Button, { ButtonIcon } from "../../../components/Button/Button"
import { PopupHelpButton } from "../../../components/Help/PopupHelpButton"
import ModalPortal from "../../../components/Modal/ModalPortal"
import { TitlePageResponsiveIconSmall } from "../../../components/Titles/Titles"
import THEME from "../../../configuration/Theme"
import { SIZE } from "../../../configuration/Sizes"
import type { RestaurantReservation } from "../../../Objects/RestaurantReservation/RestaurantReservationTypes"
import defaultStyle from "../../../styles/defaultPageStyle.module.css"
import GetSpace from "../../../utils/space"

interface ReservationDetailsPopupProps {
  reservation: RestaurantReservation
  onClose: () => void
  onEdit: () => void
  onCancel: () => void
}

export const ReservationDetailsPopup: React.FC<ReservationDetailsPopupProps> = ({
  reservation,
  onClose,
  onEdit,
  onCancel,
}) => {
  const { t } = useTranslation("restaurantReservations")

  const startsAt = new Date(reservation.startsAt)

  const isConfirmed = reservation.status === "Confirmed"

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <section className={defaultStyle.alert}>
            <TitlePageResponsiveIconSmall
              title={t("management.details_title")}
              icon={faCalendarCheck}
              iconColor={THEME.colors.iconBGColor}
              children={
                <div className={defaultStyle.ButtonsActions}>
                  <PopupHelpButton
                    helpKey="restaurant-reservation-details"
                    variant="global"
                    title={t("help.details.title")}
                    description={t("help.details.description")}
                    bullets={t("help.details.bullets", { returnObjects: true }) as string[]}
                  />
                  <GetSpace right={5} />

                  <ButtonIcon
                    sizeNumber={SIZE.buttonCloseIconSize}
                    bgColor={THEME.colors.buttonBGCloseColor}
                    iconColor={THEME.colors.buttonFGCloseColor}
                    icon={faClose}
                    onClick={onClose}
                    size="small"
                    descriptionKey="interactionDescriptions:restaurantReservations.detailsPopup.close"
                  />
                </div>
              }
            />

            <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
              <div className="space-y-4 p-3">
                <div className="rounded-lg border border-slate-200 bg-white p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h3 className="text-lg font-black text-slate-900">{reservation.customerName}</h3>

                      <p className="mt-1 text-sm font-semibold text-slate-600">{reservation.areaName}</p>
                    </div>

                    <span
                      className={`rounded-full px-3 py-1 text-xs font-black ${isConfirmed ? "bg-green-100 text-green-700" : "bg-slate-200 text-slate-600"}`}
                    >
                      {t(`status.${reservation.status}`)}
                    </span>
                  </div>
                  <dl className="mt-4 grid gap-3 border-t border-slate-200 pt-4 text-sm sm:grid-cols-2">
                    <div>
                      <dt className="font-bold text-slate-500">{t("fields.date")}</dt>

                      <dd className="font-black text-slate-900">{startsAt.toLocaleDateString("pt-BR")}</dd>
                    </div>

                    <div>
                      <dt className="font-bold text-slate-500">{t("public.steps.time")}</dt>

                      <dd className="font-black text-slate-900">
                        {startsAt.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
                      </dd>
                    </div>

                    <div>
                      <dt className="font-bold text-slate-500">{t("fields.party_size")}</dt>

                      <dd className="font-black text-slate-900">
                        {t("management.party_size_value", { count: reservation.partySize })}
                      </dd>
                    </div>

                    <div className="sm:col-span-2">
                      <dt className="font-bold text-slate-500">{t("fields.notes")}</dt>

                      <dd className="font-semibold text-slate-900">{reservation.notes || t("management.no_notes")}</dd>
                    </div>
                  </dl>
                </div>

                {isConfirmed && (
                  <div className="grid gap-2 sm:grid-cols-2">
                    <Button
                      label={t("actions.edit_reservation")}
                      onClick={onEdit}
                      bgColor={THEME.colors.buttonBGSaveColor}
                      descriptionKey="interactionDescriptions:restaurantReservations.detailsPopup.edit"
                    />

                    <Button
                      label={t("actions.cancel")}
                      onClick={onCancel}
                      bgColor={THEME.colors.buttonBGRemoveColor}
                      descriptionKey="interactionDescriptions:restaurantReservations.detailsPopup.cancel"
                    />
                  </div>
                )}

                {!isConfirmed && (
                  <p className="text-center text-sm font-bold text-slate-500">
                    <FontAwesomeIcon icon={faCalendarCheck} /> {t("management.cancelled_read_only")}
                  </p>
                )}
              </div>
            </Animate>
          </section>
        </Animate>
      </div>
    </ModalPortal>
  )
}
